/*
  <back-in-stock-klaviyo> - see snippets/back-in-stock-klaviyo.liquid for the why.

  Visibility follows the product's variant picker: Dawn publishes
  PUB_SUB_EVENTS.variantChange (global.js) with the newly selected variant;
  we show ourselves only when that variant is unavailable. As a fallback (e.g.
  a picker that doesn't publish), we also watch the product form's hidden
  input[name="id"].

  Set-up happens in connectedCallback, not the constructor: the element can be
  upgraded before its children are parsed, and quick-add modals insert it
  after load (same timing trap as the swatch-swap card, 2026-09-08).
*/
(() => {
  if (customElements.get('back-in-stock-klaviyo')) return;

  const KLAVIYO_REVISION = '2026-07-15';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  class BackInStockKlaviyo extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      try {
        this.product = JSON.parse(this.querySelector('[data-bis-product]').textContent);
      } catch (e) {
        return;
      }
      this.sectionId = this.dataset.sectionId;
      this.toggleButton = this.querySelector('[data-bis-toggle]');
      this.form = this.querySelector('[data-bis-form]');
      this.label = this.querySelector('[data-bis-label]');
      this.message = this.querySelector('[data-bis-message]');
      this.submitButton = this.querySelector('[data-bis-submit]');

      this.toggleButton.addEventListener('click', () => this.setOpen(this.form.hidden));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));

      this.setVariant(this.dataset.initialVariant);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event || !event.data || event.data.sectionId !== this.sectionId) return;
          const variant = event.data.variant;
          this.setVariant(variant ? variant.id : null, variant);
        });
      }

      const idInput = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
      if (idInput) {
        this.onIdChange = () => this.setVariant(idInput.value);
        idInput.addEventListener('change', this.onIdChange);
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    setVariant(id, eventVariant) {
      const variant = this.product.variants.find((v) => String(v.id) === String(id));
      const available = eventVariant && typeof eventVariant.available === 'boolean'
        ? eventVariant.available
        : variant ? variant.available : true;

      if (this.variant && variant && this.variant.id === variant.id && this.hidden === available) return;

      this.variant = variant || null;
      this.hidden = !variant || available;
      this.resetForm();
      if (variant) {
        const size = variant.title === 'Default Title' ? '' : `size ${variant.title}`;
        const colour = this.product.colour ? `${this.product.colour} ` : '';
        this.label.textContent = size ? `${colour}${size}`.trim() : 'this item';
      }
    }

    setOpen(open) {
      this.form.hidden = !open;
      this.toggleButton.setAttribute('aria-expanded', String(open));
      if (open) {
        const first = this.form.querySelector('input');
        if (first) first.focus();
      }
    }

    resetForm() {
      this.setOpen(false);
      this.form.classList.remove('is-done');
      this.classList.remove('is-done');
      this.message.textContent = '';
      this.message.classList.remove('is-error');
      this.submitButton.disabled = false;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.variant) return;

      const firstName = this.form.elements.first_name.value.trim();
      const email = this.form.elements.email.value.trim();
      if (!firstName || !EMAIL_RE.test(email)) {
        this.showMessage('Please enter your first name and a valid email address.', true);
        return;
      }

      this.submitButton.disabled = true;
      this.showMessage('Saving…', false);

      const base = 'https://a.klaviyo.com/client';
      const query = `?company_id=${encodeURIComponent(this.dataset.companyId)}`;
      const headers = { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION };

      const subscription = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${this.variant.id}` } },
          },
        },
      };

      const requestEvent = {
        data: {
          type: 'event',
          attributes: {
            properties: {
              ProductName: this.product.title,
              ProductID: this.product.id,
              ProductHandle: this.product.handle,
              ProductURL: this.product.url,
              ImageURL: this.product.image,
              Colour: this.product.colour,
              Size: this.variant.title,
              VariantID: this.variant.id,
              SKU: this.variant.sku,
            },
            metric: { data: { type: 'metric', attributes: { name: 'Back in Stock Requested' } } },
            profile: { data: { type: 'profile', attributes: { email, first_name: firstName } } },
          },
        },
      };

      try {
        const [subRes, eventRes] = await Promise.all([
          fetch(`${base}/back-in-stock-subscriptions/${query}`, { method: 'POST', headers, body: JSON.stringify(subscription) }),
          fetch(`${base}/events/${query}`, { method: 'POST', headers, body: JSON.stringify(requestEvent) }),
        ]);
        if (!subRes.ok) throw new Error(`subscription ${subRes.status}`);
        if (!eventRes.ok) console.warn('Back in stock: request event not recorded', eventRes.status);

        this.form.classList.add('is-done');
        this.classList.add('is-done');
        this.showMessage(`Thanks ${firstName} - we'll email you when ${this.label.textContent} is back in stock.`, false);
      } catch (error) {
        console.warn('Back in stock signup failed', error);
        this.submitButton.disabled = false;
        this.showMessage('Sorry, something went wrong. Please try again.', true);
      }
    }

    showMessage(text, isError) {
      this.message.textContent = text;
      this.message.classList.toggle('is-error', Boolean(isError));
    }
  }

  customElements.define('back-in-stock-klaviyo', BackInStockKlaviyo);
})();
